import { useState, useEffect } from "react";
import { toast } from "sonner";
import { adminApi, type SystemSetting, type BlockedIp } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Save,
  Check,
  AlertTriangle,
  RotateCcw,
  Settings,
  Gauge,
  Building2,
  Shield,
  MoreHorizontal,
  Trash2,
  Ban,
  Unlock,
  RefreshCw,
  Plus,
  X,
} from "lucide-react";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";

// ─── Settings metadata ────────────────────────────────────────────────
type SettingInputType = "auto" | "textarea" | "ip-list" | "json";

interface SettingMeta {
  label: string;
  description: string;
  defaultValue: unknown;
  suffix?: string;
  placeholder?: string;
  /** Custom input type for special fields. Defaults to "auto" which infers from value type. */
  inputType?: SettingInputType;
  /** Hint text shown below the input for complex fields */
  hint?: string;
  validate?: (v: unknown) => string | null;
}

const SETTING_META: Record<string, SettingMeta> = {
  // ── System ──────────────────────────────────────────────────────────
  maintenance_mode: {
    label: "Underhållsläge (API)",
    description:
      "Sätter API:t i underhållsläge. Inloggade användare får 503 på alla API-anrop — endast /health och /admin-endpoints fortsätter fungera. Publika sidor visas fortfarande.",
    defaultValue: false,
  },
  full_maintenance_mode: {
    label: "Fullständigt underhållsläge",
    description:
      "Stänger hela webbplatsen inklusive publika sidor. Alla besökare ser en underhållssida. Admins och IP-adresser i whitelist kan fortfarande nå sidan.",
    defaultValue: false,
  },
  maintenance_ip_whitelist: {
    label: "IP-undantag för underhåll",
    description:
      "IP-adresser som kan nå sidan trots underhållsläge. Ange en IP per rad. Använd för utvecklare, testare eller externa parter som behöver åtkomst.",
    defaultValue: [],
    inputType: "ip-list",
    placeholder: "192.168.1.100",
    hint: "Ange en IP-adress per rad. Stödjer IPv4 och IPv6.",
  },
  maintenance_message: {
    label: "Underhållsmeddelande",
    description:
      "Anpassat meddelande som visas på underhållssidan. Lämna tomt för standardmeddelande.",
    defaultValue: "",
    inputType: "textarea",
    placeholder: "Vi genomför planerat underhåll och är snart tillbaka...",
    hint: "Visas på underhållssidan istället för standardtexten.",
  },
  maintenance_estimated_end: {
    label: "Beräknad sluttid",
    description:
      "När underhållet beräknas vara klart. Visas på underhållssidan så besökare vet när de kan förvänta sig att sidan är tillbaka.",
    defaultValue: "",
    placeholder: "2026-02-02T15:00:00",
    hint: "Format: ÅÅÅÅ-MM-DDTHH:MM:SS (t.ex. 2026-02-02T15:00:00). Lämna tomt om okänt.",
  },
  registration_enabled: {
    label: "Registrering aktiverad",
    description:
      "Styr om nya användare kan skapa konto via registreringsformuläret. Vid avstängt returneras 403 på registreringsanropet. Befintliga användare påverkas inte.",
    defaultValue: true,
  },
  soft_delete_retention_days: {
    label: "Ångerperiod vid radering",
    description:
      "Hur länge soft-deleted objekt (användare, företag, rapporter) bevaras innan de raderas permanent av cleanup-jobbet. Under denna period kan admin återställa objekten.",
    defaultValue: 30,
    suffix: "dagar",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 365) return "Måste vara ett heltal mellan 1 och 365";
      return null;
    },
  },
  report_edit_window_days: {
    label: "Redigeringsfönster för årsredovisningar",
    description:
      "Antal dagar efter att en årsredovisning slutförts eller exporterats som den fortfarande kan redigeras. Efter denna period låses rapporten permanent och användaren ser en banner om detta. Sätt till 0 för att inaktivera låsning helt.",
    defaultValue: 30,
    suffix: "dagar",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 0 || n > 365) return "Måste vara ett heltal mellan 0 och 365";
      return null;
    },
  },
  subscription_description: {
    label: "Prenumerationsbeskrivning",
    description:
      "Texten som visas i betalmodalen för att beskriva vad prenumerationen inkluderar. Ändra vid kampanjer eller om erbjudandet uppdateras.",
    defaultValue: "Prenumeration — alla funktioner på Kvanten",
    inputType: "textarea",
    placeholder: "T.ex. 'Prenumeration — alla funktioner på Kvanten'",
    hint: "Texten visas i betalmodalen under prisuppgifter.",
  },
  // ── Begränsningar ───────────────────────────────────────────────────
  max_companies_per_user: {
    label: "Max företag per användare",
    description:
      "Övre gräns för hur många företag en enskild användare kan skapa. Vid gränsen blockeras \"Skapa företag\" och användaren ser ett felmeddelande. Påverkar inte befintliga företag.",
    defaultValue: 50,
    suffix: "st",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 10000) return "Måste vara ett heltal mellan 1 och 10 000";
      return null;
    },
  },
  max_file_size_mb: {
    label: "Max filstorlek (SIE-uppladdning)",
    description:
      "Maximal filstorlek i MB vid uppladdning av SIE-filer. Filer som överskrider gränsen avvisas med ett felmeddelande. De flesta SIE-filer är under 1 MB.",
    defaultValue: 10,
    suffix: "MB",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 100) return "Måste vara ett heltal mellan 1 och 100";
      return null;
    },
  },
  max_sessions_per_user: {
    label: "Max sessioner per användare",
    description:
      "Högsta antal samtidiga inloggningar per användare. Om gränsen nås vid ny inloggning loggas den äldsta sessionen ut automatiskt. Skyddar mot obehörig kontodelning.",
    defaultValue: 5,
    suffix: "st",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 100)
        return "Måste vara ett heltal mellan 1 och 100";
      return null;
    },
  },
  // ── Företagsuppgifter ─────────────────────────────────────────────
  company_name: {
    label: "Företagsnamn",
    description: "Företagsnamn som visas på kvitton, fakturor och andra officiella dokument.",
    defaultValue: "Kvanten",
    placeholder: "Kvanten",
  },
  company_org_number: {
    label: "Organisationsnummer",
    description: "Organisationsnummer (format: XXXXXX-XXXX). Visas på kvitton och fakturor.",
    defaultValue: "",
    placeholder: "559XXX-XXXX",
  },
  company_address: {
    label: "Gatuadress",
    description: "Gatuadress. Visas i säljarinformation på kvitton och fakturor.",
    defaultValue: "",
    placeholder: "Exempelgatan 1",
  },
  company_postal_code: {
    label: "Postnummer",
    description: "Postnummer. Visas i säljarinformation på kvitton och fakturor.",
    defaultValue: "",
    placeholder: "111 22",
  },
  company_city: {
    label: "Ort",
    description: "Ort. Visas i säljarinformation på kvitton och fakturor.",
    defaultValue: "",
    placeholder: "Stockholm",
  },
  company_email: {
    label: "E-postadress",
    description: "Kontakt-e-post. Visas i säljarinformation på kvitton och fakturor.",
    defaultValue: "",
    placeholder: "info@kvanten.se",
  },
  company_phone: {
    label: "Telefonnummer",
    description: "Telefonnummer. Visas i säljarinformation på kvitton och fakturor.",
    defaultValue: "",
    placeholder: "08-123 456 78",
  },
  company_vat_number: {
    label: "Momsregistreringsnummer",
    description: "Momsregistreringsnummer (format: SE + org.nr utan bindestreck + 01). Visas på kvitton.",
    defaultValue: "",
    placeholder: "SE559XXXXXXX01",
  },
  // ── Rate Limiting — Globalt ─────────────────────────────────────────
  rate_limit_public_rpm: {
    label: "Publika anrop — max per tidsfönster",
    description:
      "Max antal API-anrop som en ej inloggad besökare (per IP) får göra inom det publika tidsfönstret. Gäller alla endpoints. Vid överskridande returneras HTTP 429.",
    defaultValue: 30,
    suffix: "req",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 10000) return "Måste vara ett heltal mellan 1 och 10 000";
      return null;
    },
  },
  rate_limit_public_window_ms: {
    label: "Publikt tidsfönster",
    description:
      "Tidsperioden i millisekunder som det publika maxantalet gäller inom. Tillsammans med \"Publika anrop — max\" bildar dessa ett par: t.ex. 30 anrop per 60 000 ms (1 minut).",
    defaultValue: 60000,
    suffix: "ms",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1000 || n > 86400000) return "Måste vara mellan 1 000 och 86 400 000 ms";
      return null;
    },
  },
  rate_limit_auth_rpm: {
    label: "Inloggade anrop — max per tidsfönster",
    description:
      "Max antal API-anrop som en inloggad användare (per IP) får göra inom det inloggade tidsfönstret. Gäller alla endpoints. Vid överskridande returneras HTTP 429.",
    defaultValue: 100,
    suffix: "req",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 10000) return "Måste vara ett heltal mellan 1 och 10 000";
      return null;
    },
  },
  rate_limit_auth_window_ms: {
    label: "Inloggat tidsfönster",
    description:
      "Tidsperioden i millisekunder som det inloggade maxantalet gäller inom. Tillsammans med \"Inloggade anrop — max\" bildar dessa ett par: t.ex. 100 anrop per 60 000 ms (1 minut).",
    defaultValue: 60000,
    suffix: "ms",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1000 || n > 86400000) return "Måste vara mellan 1 000 och 86 400 000 ms";
      return null;
    },
  },
  rate_limit_admin_rpm: {
    label: "Admin-anrop — max per tidsfönster",
    description:
      "Max antal API-anrop som en admin-användare (per IP) får göra inom admin-tidsfönstret. Satt högre för att inte störa admin-arbete.",
    defaultValue: 500,
    suffix: "req",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 50000) return "Måste vara ett heltal mellan 1 och 50 000";
      return null;
    },
  },
  rate_limit_admin_window_ms: {
    label: "Admin-tidsfönster",
    description:
      "Tidsperioden i millisekunder som admin-maxantalet gäller inom. Tillsammans med \"Admin-anrop — max\" bildar dessa ett par: t.ex. 500 anrop per 60 000 ms (1 minut).",
    defaultValue: 60000,
    suffix: "ms",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1000 || n > 86400000) return "Måste vara mellan 1 000 och 86 400 000 ms";
      return null;
    },
  },
  rate_limit_login_max: {
    label: "Max inloggningsförsök",
    description:
      "Max antal inloggningsförsök per IP-adress inom inloggningsfönstret. Skyddar mot brute force-attacker. Vid överskridande blockeras fler försök tills fönstret nollställs.",
    defaultValue: 5,
    suffix: "st",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 100) return "Måste vara ett heltal mellan 1 och 100";
      return null;
    },
  },
  rate_limit_login_window_ms: {
    label: "Inloggningsfönster",
    description:
      "Tidsperioden som max inloggningsförsök gäller inom. Tillsammans med \"Max inloggningsförsök\" bildar dessa ett par: t.ex. 5 försök per 900 000 ms (15 minuter).",
    defaultValue: 900000,
    suffix: "ms",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 10000 || n > 86400000) return "Måste vara mellan 10 000 och 86 400 000 ms";
      return null;
    },
  },
  rate_limit_register_max: {
    label: "Max registreringar",
    description:
      "Max antal registreringsförsök per IP-adress inom registreringsfönstret. Skyddar mot automatiserad mass-registrering av konton.",
    defaultValue: 3,
    suffix: "st",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 50) return "Måste vara ett heltal mellan 1 och 50";
      return null;
    },
  },
  rate_limit_register_window_ms: {
    label: "Registreringsfönster",
    description:
      "Tidsperioden som max registreringar gäller inom. Tillsammans med \"Max registreringar\" bildar dessa ett par: t.ex. 3 registreringar per 3 600 000 ms (1 timme).",
    defaultValue: 3600000,
    suffix: "ms",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 10000 || n > 86400000) return "Måste vara mellan 10 000 och 86 400 000 ms";
      return null;
    },
  },
  rate_limit_upload_sie_max: {
    label: "Max SIE-uppladdningar",
    description:
      "Max antal SIE-filuppladdningar per IP-adress inom uppladdningsfönstret. Parsning av SIE-filer är resurskrävande — denna gräns skyddar servern mot överbelastning.",
    defaultValue: 5,
    suffix: "st",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 100) return "Måste vara ett heltal mellan 1 och 100";
      return null;
    },
  },
  rate_limit_upload_sie_window_ms: {
    label: "SIE-uppladdningsfönster",
    description:
      "Tidsperioden som max SIE-uppladdningar gäller inom. Tillsammans med \"Max SIE-uppladdningar\" bildar dessa ett par: t.ex. 5 uppladdningar per 60 000 ms (1 minut).",
    defaultValue: 60000,
    suffix: "ms",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1000 || n > 86400000) return "Måste vara mellan 1 000 och 86 400 000 ms";
      return null;
    },
  },
  rate_limit_export_max: {
    label: "Max PDF-export-anrop",
    description:
      "Max antal PDF-exportanrop per IP-adress inom exportfönstret. PDF-generering är resurskrävande — denna gräns skyddar servern mot överbelastning.",
    defaultValue: 3,
    suffix: "st",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 100) return "Måste vara ett heltal mellan 1 och 100";
      return null;
    },
  },
  rate_limit_export_window_ms: {
    label: "Exportfönster",
    description:
      "Tidsperioden som max PDF-export-anrop gäller inom. Tillsammans med \"Max PDF-export-anrop\" bildar dessa ett par: t.ex. 3 nedladdningar per 60 000 ms (1 minut).",
    defaultValue: 60000,
    suffix: "ms",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1000 || n > 86400000) return "Måste vara mellan 1 000 och 86 400 000 ms";
      return null;
    },
  },
  // ── IP-blockering ───────────────────────────────────────────────────
  ip_block_threshold: {
    label: "Tröskelvärde för IP-blockering",
    description:
      "Antal misslyckade inloggningsförsök från samma IP-adress inom tidsfönstret innan IP:n blockeras helt. Detta är en persistent blockering som varar i blockerings­tiden (t.ex. 24 timmar), till skillnad från rate limiting som nollställs efter fönstret.",
    defaultValue: 10,
    suffix: "st",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 3 || n > 100) return "Måste vara ett heltal mellan 3 och 100";
      return null;
    },
  },
  ip_block_window_ms: {
    label: "Fönster för misslyckade försök",
    description:
      "Tidsperioden i millisekunder under vilken misslyckade inloggningsförsök räknas. Om tröskelvärdet uppnås inom detta fönster blockeras IP:n. 900 000 = 15 minuter.",
    defaultValue: 900000,
    suffix: "ms",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 60000 || n > 86400000) return "Måste vara mellan 60 000 och 86 400 000 ms";
      return null;
    },
  },
  ip_block_duration_ms: {
    label: "Blockeringstid",
    description:
      "Hur länge en blockerad IP-adress förblir blockerad innan den automatiskt avblockeras. 86 400 000 = 24 timmar. Admin kan manuellt avblockera tidigare.",
    defaultValue: 86400000,
    suffix: "ms",
    validate: (v) => {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 60000 || n > 604800000) return "Måste vara mellan 60 000 och 604 800 000 ms (1 minut till 7 dagar)";
      return null;
    },
  },
  ip_whitelist: {
    label: "Vitlistade IP-adresser",
    description:
      "IP-adresser som aldrig ska blockeras, oavsett antal misslyckade försök. Användbart för kontor eller kända betrodda källor.",
    defaultValue: [],
    inputType: "ip-list",
    placeholder: "Ange IP-adress...",
    hint: "Tryck Enter eller klicka + för att lägga till. Stödjer IPv4 (t.ex. 192.168.1.100) och IPv6 (t.ex. ::1).",
    validate: (v) => {
      if (!Array.isArray(v)) return "Måste vara en lista";
      const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::1$|^::$/;
      for (const ip of v) {
        if (typeof ip !== "string" || (!ipRegex.test(ip) && ip !== "localhost")) {
          return `Ogiltig IP-adress: ${ip}`;
        }
      }
      return null;
    },
  },
};

// Keys managed on the Pricing page — excluded from this view
const PRICING_KEYS = ["base_price", "vat_rate", "volume_discounts", "campaigns", "bureau_tiers"];

const RATE_LIMIT_KEYS = [
  "rate_limit_public_rpm", "rate_limit_public_window_ms",
  "rate_limit_auth_rpm", "rate_limit_auth_window_ms",
  "rate_limit_admin_rpm", "rate_limit_admin_window_ms",
  "rate_limit_login_max", "rate_limit_login_window_ms",
  "rate_limit_register_max", "rate_limit_register_window_ms",
  "rate_limit_upload_sie_max", "rate_limit_upload_sie_window_ms",
  "rate_limit_export_max", "rate_limit_export_window_ms",
];

const IP_BLOCK_KEYS = [
  "ip_block_threshold", "ip_block_window_ms", "ip_block_duration_ms", "ip_whitelist",
];

const COMPANY_INFO_KEYS = [
  "company_name", "company_org_number", "company_address",
  "company_postal_code", "company_city", "company_email",
  "company_phone", "company_vat_number",
];

const getMetaFor = (key: string): SettingMeta | null => SETTING_META[key] ?? null;

// ─── Tab definitions ──────────────────────────────────────────────────
interface Tab {
  id: string;
  label: string;
  icon: React.ElementType;
  description: string;
}

const TABS: Tab[] = [
  { id: "general", label: "Allmänt", icon: Settings, description: "Driftstatus, systemkritiska inställningar och innehåll." },
  { id: "limits", label: "Begränsningar", icon: Gauge, description: "Gränsvärden för användare och uppladdningar." },
  { id: "company", label: "Företagsuppgifter", icon: Building2, description: "Säljarinformation som visas på kvitton och fakturor." },
  { id: "security", label: "Säkerhet", icon: Shield, description: "Rate limiting — begränsning av API-anrop. Ändringar aktiveras inom 30 sekunder." },
  { id: "other", label: "Övrigt", icon: MoreHorizontal, description: "Övriga systeminställningar." },
];

// ─── IP List Input Component ─────────────────────────────────────────
interface IpListInputProps {
  currentIps: string[];
  onAdd: (ip: string) => void;
  onRemove: (ip: string) => void;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
}

function IpListInput({ currentIps, onAdd, onRemove, placeholder, hint, disabled }: IpListInputProps) {
  const [newIp, setNewIp] = useState("");

  const handleAddIp = () => {
    const ip = newIp.trim();
    if (!ip) return;
    // Basic IP validation
    const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::1$|^::$|^(?:[0-9a-fA-F]{1,4}:)*:[0-9a-fA-F]{1,4}$/;
    if (!ipv4Regex.test(ip) && !ipv6Regex.test(ip) && ip !== "localhost") {
      toast.error("Ogiltig IP-adress. Ange en giltig IPv4 eller IPv6-adress.");
      return;
    }
    if (currentIps.includes(ip)) {
      toast.error("IP-adressen finns redan i listan.");
      return;
    }
    onAdd(ip);
    setNewIp("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddIp();
    }
  };

  return (
    <div className="w-full sm:max-w-md space-y-3">
      {/* Input row */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newIp}
          onChange={(e) => setNewIp(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder || "Ange IP-adress..."}
          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddIp}
          disabled={disabled || !newIp.trim()}
          className="px-3 flex-shrink-0"
        >
          <Plus size={16} />
        </Button>
      </div>
      {/* Hint text */}
      {hint && (
        <p className="text-xs text-gray-400">{hint}</p>
      )}
      {/* IP tags */}
      {currentIps.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {currentIps.map((ip) => (
            <span
              key={ip}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 border border-gray-200 rounded-full text-sm font-mono text-gray-700"
            >
              {ip}
              {!disabled && (
                <button
                  onClick={() => onRemove(ip)}
                  className="p-0.5 hover:bg-gray-200 rounded-full transition-colors"
                  title="Ta bort"
                >
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {currentIps.length === 0 && (
        <p className="text-xs text-gray-400 italic">Inga IP-adresser tillagda.</p>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────

interface EditableValue {
  value: string;
  isDirty: boolean;
  isSaving: boolean;
  saved: boolean;
  error: string | null;
}

/** Check if a setting stores a text string (as opposed to number/boolean/JSON). */
function isTextSetting(setting: SystemSetting): boolean {
  return typeof setting.value === "string";
}

export function AdminSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editValues, setEditValues] = useState<Record<string, EditableValue>>({});
  const [activeTab, setActiveTab] = useState("general");
  const [isRunningCleanup, setIsRunningCleanup] = useState(false);

  // Blocked IPs state
  const [blockedIps, setBlockedIps] = useState<BlockedIp[]>([]);
  const [isLoadingBlockedIps, setIsLoadingBlockedIps] = useState(false);
  const [unblockingIp, setUnblockingIp] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  // Load blocked IPs when security tab is active
  useEffect(() => {
    if (activeTab === "security") {
      loadBlockedIps();
    }
  }, [activeTab]);

  const loadBlockedIps = async () => {
    setIsLoadingBlockedIps(true);
    try {
      const response = await adminApi.getBlockedIps();
      if (response.success && response.data) {
        setBlockedIps(response.data);
      }
    } catch {
      console.error("Failed to load blocked IPs");
    } finally {
      setIsLoadingBlockedIps(false);
    }
  };

  const handleUnblockIp = async (ip: string) => {
    if (!confirm(`Är du säker på att du vill avblockera IP-adressen ${ip}?`)) {
      return;
    }
    setUnblockingIp(ip);
    try {
      const response = await adminApi.unblockIp(ip);
      if (response.success) {
        toast.success(`IP-adressen ${ip} har avblockerats`);
        setBlockedIps((prev) => prev.filter((b) => b.ip !== ip));
      } else {
        toast.error(response.error?.message || "Kunde inte avblockera IP-adressen");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setUnblockingIp(null);
    }
  };

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const response = await adminApi.getSettings();
      if (response.success && response.data) {
        setSettings(response.data);
        const initial: Record<string, EditableValue> = {};
        for (const s of response.data) {
          // For text/string values, store the raw string (no JSON quotes).
          // For numbers/booleans/objects, use JSON.stringify.
          initial[s.key] = {
            value: typeof s.value === "string" ? s.value : JSON.stringify(s.value),
            isDirty: false,
            isSaving: false,
            saved: false,
            error: null,
          };
        }
        setEditValues(initial);
      }
    } catch {
      console.error("Failed to load settings");
      toast.error("Kunde inte ladda systeminställningar");
    } finally {
      setIsLoading(false);
    }
  };

  const handleValueChange = (key: string, newValue: string) => {
    setEditValues((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: newValue,
        isDirty: true,
        saved: false,
        error: null,
      },
    }));
  };

  const handleResetToDefault = (key: string) => {
    const meta = getMetaFor(key);
    if (!meta) return;
    const setting = settings.find((s) => s.key === key);
    const isText = setting ? isTextSetting(setting) : typeof meta.defaultValue === "string";
    const defaultStr = isText ? String(meta.defaultValue) : JSON.stringify(meta.defaultValue);
    setEditValues((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: defaultStr,
        isDirty: true,
        saved: false,
        error: null,
      },
    }));
  };

  const handleSave = async (key: string) => {
    const editValue = editValues[key];
    if (!editValue || !editValue.isDirty) return;

    const setting = settings.find((s) => s.key === key);
    const isText = setting ? isTextSetting(setting) : false;

    // Parse the value
    let parsedValue: unknown;
    if (isText) {
      // Text settings: use raw string value directly
      parsedValue = editValue.value;
    } else {
      try {
        parsedValue = JSON.parse(editValue.value);
      } catch {
        setEditValues((prev) => ({
          ...prev,
          [key]: { ...prev[key], error: "Ogiltigt värde" },
        }));
        return;
      }
    }

    // Run validation if available
    const meta = getMetaFor(key);
    if (meta?.validate) {
      const validationError = meta.validate(parsedValue);
      if (validationError) {
        setEditValues((prev) => ({
          ...prev,
          [key]: { ...prev[key], error: validationError },
        }));
        return;
      }
    }

    setEditValues((prev) => ({
      ...prev,
      [key]: { ...prev[key], isSaving: true, error: null },
    }));

    try {
      const description = meta?.description || settings.find((s) => s.key === key)?.description || undefined;
      const response = await adminApi.updateSetting(key, parsedValue, description);
      if (response.success) {
        toast.success("Inställningen har sparats");
        setEditValues((prev) => ({
          ...prev,
          [key]: { ...prev[key], isDirty: false, isSaving: false, saved: true },
        }));
        setTimeout(() => {
          setEditValues((prev) => ({
            ...prev,
            [key]: prev[key] ? { ...prev[key], saved: false } : prev[key],
          }));
        }, 2000);
      } else {
        setEditValues((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            isSaving: false,
            error: response.error?.message || "Kunde inte spara",
          },
        }));
      }
    } catch {
      setEditValues((prev) => ({
        ...prev,
        [key]: { ...prev[key], isSaving: false, error: "Kunde inte ansluta till servern" },
      }));
    }
  };

  const handleCleanup = async () => {
    if (!confirm("Är du säker på att du vill permanent radera alla soft-deleted objekt som är äldre än ångerperioden? Detta kan inte ångras.")) {
      return;
    }
    setIsRunningCleanup(true);
    try {
      const response = await adminApi.cleanupSoftDeleted();
      if (response.success && response.data) {
        const { users, companies, reports, roles, total, retentionDays } = response.data;
        if (total === 0) {
          toast.info(`Inga objekt att radera. Ångerperioden är ${retentionDays} dagar.`);
        } else {
          toast.success(
            `Rensning klar: ${total} objekt raderade permanent (${users} användare, ${companies} företag, ${reports} rapporter, ${roles} roller)`
          );
        }
      } else {
        toast.error(response.error?.message || "Kunde inte köra rensning");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsRunningCleanup(false);
    }
  };

  const isSuperAdmin = user?.role === "super_admin";
  const protectedKeys = ["maintenance_mode", "full_maintenance_mode", "maintenance_ip_whitelist", "registration_enabled"];

  const getInputType = (value: unknown): "boolean" | "number" | "text" => {
    if (typeof value === "boolean") return "boolean";
    if (typeof value === "number") return "number";
    return "text";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  // Group settings by tab
  const knownKeys = [...Object.keys(SETTING_META), ...PRICING_KEYS];
  const groupedSettings: Record<string, SystemSetting[]> = {
    general: settings.filter((s) =>
      ["maintenance_mode", "full_maintenance_mode", "maintenance_ip_whitelist", "maintenance_message", "maintenance_estimated_end", "registration_enabled", "soft_delete_retention_days", "report_edit_window_days", "subscription_description"].includes(s.key)
    ),
    limits: settings.filter((s) =>
      ["max_companies_per_user", "max_file_size_mb", "max_sessions_per_user"].includes(s.key)
    ),
    company: settings.filter((s) => COMPANY_INFO_KEYS.includes(s.key)),
    security: settings.filter((s) => RATE_LIMIT_KEYS.includes(s.key) || IP_BLOCK_KEYS.includes(s.key)),
    other: settings.filter((s) => !knownKeys.includes(s.key) && !COMPANY_INFO_KEYS.includes(s.key)),
  };

  const renderSettingRow = (setting: SystemSetting) => {
    const editValue = editValues[setting.key];
    if (!editValue) return null;

    const isProtected = protectedKeys.includes(setting.key) && !isSuperAdmin;
    const inputType = getInputType(setting.value);
    const meta = getMetaFor(setting.key);
    const isText = isTextSetting(setting);
    const customInputType = meta?.inputType || "auto";

    const label = meta?.label || setting.key;
    const description = meta?.description || setting.description || null;
    const isDefault = meta
      ? editValue.value === (isText ? String(meta.defaultValue) : JSON.stringify(meta.defaultValue))
      : true;

    // Check if this is a "wide" input type that needs full-width layout
    const isWideInput = customInputType === "textarea" || customInputType === "ip-list";

    // For wide inputs, use a stacked layout instead of side-by-side
    if (isWideInput) {
      return (
        <div key={setting.key} className="py-4 space-y-3">
          {/* Header row - stacks on mobile */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium text-gray-900">{label}</p>
                {isProtected && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700">
                    <AlertTriangle size={10} />
                    Super Admin
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 font-mono mt-0.5 break-all">{setting.key}</p>
              {description && (
                <p className="text-xs text-gray-500 mt-1">{description}</p>
              )}
            </div>
            {/* Action buttons - full width on mobile */}
            <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
              {meta && !isProtected && !isDefault && (
                <button
                  onClick={() => handleResetToDefault(setting.key)}
                  title={`Återställ till standard`}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                >
                  <RotateCcw size={14} />
                </button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSave(setting.key)}
                disabled={!editValue.isDirty || editValue.isSaving || isProtected}
                className="flex-1 sm:flex-initial sm:w-20"
              >
                {editValue.isSaving ? (
                  <Spinner size="sm" />
                ) : editValue.saved ? (
                  <>
                    <Check size={14} className="text-green-600" />
                    <span className="text-green-600">Sparat</span>
                  </>
                ) : (
                  <>
                    <Save size={14} />
                    Spara
                  </>
                )}
              </Button>
            </div>
          </div>
          {/* Input area */}
          <div>
            {customInputType === "textarea" && (
              <div className="w-full sm:max-w-md space-y-2">
                <textarea
                  value={editValue.value}
                  onChange={(e) => handleValueChange(setting.key, e.target.value)}
                  disabled={isProtected}
                  placeholder={meta?.placeholder}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 resize-y"
                />
                {meta?.hint && (
                  <p className="text-xs text-gray-400">{meta.hint}</p>
                )}
              </div>
            )}
            {customInputType === "ip-list" && (() => {
              // Parse current IPs from JSON
              let currentIps: string[] = [];
              try {
                currentIps = JSON.parse(editValue.value);
                if (!Array.isArray(currentIps)) currentIps = [];
              } catch {
                currentIps = [];
              }
              return (
                <IpListInput
                  currentIps={currentIps}
                  onAdd={(ip) => {
                    const updated = [...currentIps, ip];
                    handleValueChange(setting.key, JSON.stringify(updated));
                  }}
                  onRemove={(ip) => {
                    const updated = currentIps.filter((i) => i !== ip);
                    handleValueChange(setting.key, JSON.stringify(updated));
                  }}
                  placeholder={meta?.placeholder}
                  hint={meta?.hint}
                  disabled={isProtected}
                />
              );
            })()}
          </div>
          {editValue.error && (
            <p className="text-xs text-red-600">{editValue.error}</p>
          )}
        </div>
      );
    }

    // Standard layout for simple inputs - stacks vertically on mobile
    return (
      <div key={setting.key} className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 py-4">
        {/* Label and description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-gray-900">{label}</p>
            {isProtected && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700">
                <AlertTriangle size={10} />
                Super Admin
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 font-mono mt-0.5 break-all">{setting.key}</p>
          {description && (
            <p className="text-xs text-gray-500 mt-1">{description}</p>
          )}
          {editValue.error && (
            <p className="text-xs text-red-600 mt-1">{editValue.error}</p>
          )}
        </div>
        {/* Input and actions - full width on mobile */}
        <div className="flex items-center gap-2 w-full sm:w-auto flex-shrink-0">
          {inputType === "boolean" ? (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={editValue.value === "true"}
                onChange={(e) => handleValueChange(setting.key, String(e.target.checked))}
                disabled={isProtected}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 peer-disabled:opacity-50" />
            </label>
          ) : isText ? (
            <div className="flex items-center gap-1.5 flex-1 sm:flex-initial">
              <input
                type="text"
                value={editValue.value}
                onChange={(e) => handleValueChange(setting.key, e.target.value)}
                disabled={isProtected}
                placeholder={meta?.placeholder}
                className="w-full sm:w-56 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
              />
            </div>
          ) : (
            <div className="flex items-center gap-1.5 flex-1 sm:flex-initial">
              <input
                type={inputType === "number" ? "number" : "text"}
                value={editValue.value}
                onChange={(e) => handleValueChange(setting.key, e.target.value)}
                disabled={isProtected}
                step={setting.key === "vat_rate" ? "0.01" : "1"}
                className="w-full sm:w-28 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
              />
              {meta?.suffix && (
                <span className="text-xs text-gray-400 w-8 flex-shrink-0">{meta.suffix}</span>
              )}
            </div>
          )}
          {meta && !isProtected && !isDefault && (
            <button
              onClick={() => handleResetToDefault(setting.key)}
              title={`Återställ till standard (${isText ? String(meta.defaultValue) : JSON.stringify(meta.defaultValue)})`}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
            >
              <RotateCcw size={14} />
            </button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSave(setting.key)}
            disabled={!editValue.isDirty || editValue.isSaving || isProtected}
            className="w-20 flex-shrink-0"
          >
            {editValue.isSaving ? (
              <Spinner size="sm" />
            ) : editValue.saved ? (
              <>
                <Check size={14} className="text-green-600" />
                <span className="text-green-600">Sparat</span>
              </>
            ) : (
              <>
                <Save size={14} />
                Spara
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  const activeTabDef = TABS.find((t) => t.id === activeTab)!;
  const activeItems = groupedSettings[activeTab] || [];
  // Hide "Övrigt" tab if empty
  const visibleTabs = TABS.filter((t) => t.id !== "other" || (groupedSettings.other?.length ?? 0) > 0);

  return (
    <>
      <PageMeta title={ADMIN_PAGE_META.settings.title} noIndex />
      <div className="space-y-6 min-w-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Systeminställningar</h1>
          <p className="text-gray-600 mt-2">
            Konfigurera systemparametrar
            {!isSuperAdmin && (
              <span className="text-amber-600 ml-2">
                (Vissa inställningar kräver Super Admin)
              </span>
            )}
          </p>
        </div>

      {/* Tab navigation - horisontellt scrollbar på mobil */}
      <div className="border-b border-gray-200 -mx-4 sm:mx-0 overflow-hidden">
        <nav
          className="flex gap-1 -mb-px px-4 sm:px-0 overflow-x-auto scrollbar-hide"
          aria-label="Tabs"
        >
          {visibleTabs.map((tab) => {
            const isActive = tab.id === activeTab;
            const Icon = tab.icon;
            const count = groupedSettings[tab.id]?.length ?? 0;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                  isActive
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon size={16} />
                {tab.label}
                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                  isActive ? "bg-primary-100 text-primary-700" : "bg-gray-100 text-gray-500"
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 sm:px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">{activeTabDef.label}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{activeTabDef.description}</p>
        </div>
        <div className="px-4 sm:px-5 divide-y divide-gray-100">
          {activeItems.length > 0 ? (
            activeItems.map(renderSettingRow)
          ) : (
            <div className="py-12 text-center text-sm text-gray-400">
              Inga inställningar i denna kategori.
            </div>
          )}
        </div>
      </div>

      {/* Cleanup Section - only visible for super_admin on general tab */}
      {activeTab === "general" && isSuperAdmin && (
        <div className="bg-white rounded-lg border border-red-200">
          <div className="px-4 sm:px-5 py-4 border-b border-red-100 bg-red-50/50">
            <h2 className="text-base font-semibold text-red-900 flex items-center gap-2">
              <Trash2 size={18} className="flex-shrink-0" />
              <span>Rensa borttagna objekt</span>
            </h2>
            <p className="text-xs text-red-700 mt-0.5">
              Permanent radering av soft-deleted objekt som passerat ångerperioden
            </p>
          </div>
          <div className="px-4 sm:px-5 py-4">
            <p className="text-sm text-gray-600 mb-4">
              Kör manuell rensning för att permanent radera alla soft-deleted användare, företag,
              rapporter och roller som är äldre än den konfigurerade ångerperioden
              (för närvarande{" "}
              <strong>
                {String(settings.find((s) => s.key === "soft_delete_retention_days")?.value ?? 30)} dagar
              </strong>
              ). Detta kan inte ångras.
            </p>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleCleanup}
              disabled={isRunningCleanup}
              className="w-full sm:w-auto"
            >
              {isRunningCleanup ? (
                <>
                  <Spinner size="sm" />
                  Rensar...
                </>
              ) : (
                <>
                  <Trash2 size={14} />
                  Rensa nu
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Blocked IPs Section - only visible on security tab */}
      {activeTab === "security" && (
        <div className="bg-white rounded-lg border border-amber-200">
          <div className="px-4 sm:px-5 py-4 border-b border-amber-100 bg-amber-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-amber-900 flex items-center gap-2">
                <Ban size={18} className="flex-shrink-0" />
                <span>Blockerade IP-adresser</span>
              </h2>
              <p className="text-xs text-amber-700 mt-0.5">
                IP-adresser som blockeras på grund av för många misslyckade inloggningsförsök
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadBlockedIps}
              disabled={isLoadingBlockedIps}
              className="text-amber-700 hover:text-amber-900 hover:bg-amber-100 w-full sm:w-auto flex-shrink-0"
            >
              <RefreshCw size={14} className={isLoadingBlockedIps ? "animate-spin" : ""} />
              Uppdatera
            </Button>
          </div>
          <div className="px-4 sm:px-5 py-4">
            {isLoadingBlockedIps ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="default" />
              </div>
            ) : blockedIps.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                Inga IP-adresser är för närvarande blockerade.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 mb-3">
                  <strong>{blockedIps.length}</strong> IP-adress{blockedIps.length !== 1 ? "er" : ""} är blockerad{blockedIps.length !== 1 ? "e" : ""}.
                </p>
                <div className="border rounded-lg divide-y divide-gray-100">
                  {blockedIps.map((blocked) => {
                    const hours = Math.floor(blocked.remainingSeconds / 3600);
                    const minutes = Math.floor((blocked.remainingSeconds % 3600) / 60);
                    const timeLeft = hours > 0
                      ? `${hours}h ${minutes}m`
                      : `${minutes} min`;

                    return (
                      <div
                        key={blocked.ip}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 px-4 py-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-mono font-medium text-gray-900 break-all">
                            {blocked.ip}
                          </p>
                          <p className="text-xs text-gray-500">
                            Avblockeras automatiskt om {timeLeft}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUnblockIp(blocked.ip)}
                          disabled={unblockingIp === blocked.ip}
                          className="text-amber-700 border-amber-300 hover:bg-amber-50 w-full sm:w-auto flex-shrink-0"
                        >
                          {unblockingIp === blocked.ip ? (
                            <Spinner size="sm" />
                          ) : (
                            <>
                              <Unlock size={14} />
                              Avblockera
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
}
